import { useLayoutEffect, useRef, useState, type RefObject } from "react";
import { Button, Icons } from "@/components/ui";
import type { DictationMicControl } from "@/components/panels/input-bar-types";
import { useT } from "@/i18n";
import { toastsMessages } from "@/i18n/messages/toasts";
import { cn } from "@/lib/cn";
import { useComposerAttachments } from "@/components/panels/use-composer-attachments";
import { useComposerFileDrop } from "@/components/panels/use-composer-file-drop";
import { ImagePreviewStrip } from "@/components/panels/image-preview-strip";

/** Alias kept local so callers of the reply box don't need to know the mic
 *  shape is shared with the input bar's worker controls. */
export type ToastReplyMic = DictationMicControl;

export function ToastReplyBox({
  testId,
  value,
  onChange,
  onSend,
  placeholder,
  sending,
  mic,
  textareaRef,
}: {
  testId: string;
  value: string;
  onChange: (value: string) => void;
  onSend: (imageIds?: string[]) => void | boolean | Promise<boolean>;
  placeholder: string;
  sending: boolean;
  mic?: ToastReplyMic;
  textareaRef?: RefObject<HTMLTextAreaElement | null>;
}) {
  const t = useT(toastsMessages);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const resolvedRef = textareaRef ?? inputRef;
  const rootRef = useRef<HTMLDivElement>(null);
  const sendingRef = useRef(false);
  const [localSending, setLocalSending] = useState(false);
  const [sendError, setSendError] = useState(false);
  const { attachments, attach, remove, clearSent } = useComposerAttachments();
  const fileDrop = useComposerFileDrop({
    rootRef,
    onFile: attach,
    disabled: sending || localSending,
  });
  const [multiline, setMultiline] = useState(false);
  useLayoutEffect(() => {
    const field = resolvedRef.current;
    if (!field) return;
    const resize = () => {
      field.style.height = "0px";
      const height = Math.max(20, Math.min(96, field.scrollHeight));
      field.style.height = `${height}px`;
      setMultiline(height > 24);
    };
    resize();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(resize);
    observer.observe(field);
    return () => observer.disconnect();
  }, [resolvedRef, value]);
  const canSend =
    (value.trim().length > 0 || attachments.length > 0) &&
    !sending &&
    !localSending &&
    !attachments.some(
      (attachment) => attachment.uploading || attachment.uploadError,
    );
  const send = async () => {
    if (!canSend || sendingRef.current) return;
    sendingRef.current = true;
    setLocalSending(true);
    setSendError(false);
    const sent = attachments;
    try {
      const result = await onSend(
        sent.length
          ? sent.flatMap((attachment) =>
              attachment.imageId ? [attachment.imageId] : [],
            )
          : undefined,
      );
      if (result !== false) clearSent(sent);
      else setSendError(true);
    } catch {
      setSendError(true);
    } finally {
      sendingRef.current = false;
      setLocalSending(false);
    }
  };
  return (
    <div
      ref={rootRef}
      {...fileDrop.dropProps}
      data-file-dragging={fileDrop.dragging || undefined}
      className={cn(
        "flex min-h-9 min-w-0 flex-col bg-input-fill",
        multiline || attachments.length ? "rounded-[24px]" : "rounded-full",
      )}
      data-testid={testId}
    >
      <ImagePreviewStrip inset images={attachments} onRemove={remove} />
      <div className="flex w-full min-w-0 items-end gap-1 py-1.5 pl-3.5 pr-1.5">
        <textarea
          ref={resolvedRef}
          rows={1}
          value={value}
          placeholder={placeholder}
          aria-label={placeholder}
          onChange={(event) => onChange(event.target.value)}
          onPaste={fileDrop.handlePaste}
          onKeyDown={(event) => {
            if (
              event.key !== "Enter" ||
              event.shiftKey ||
              event.nativeEvent.isComposing
            )
              return;
            event.preventDefault();
            if (canSend) void send();
          }}
          className="max-h-24 min-h-5 min-w-0 flex-1 resize-none bg-transparent py-0.5 text-appkit-body leading-5 text-primary outline-none placeholder:text-muted"
          data-testid={`${testId}-input`}
          data-interactive="true"
        />
        {mic && (
          <Button
            type="button"
            variant="ghost"
            size="iconSm"
            aria-label={mic.recording ? t("stopDictation") : t("dictate")}
            disabled={mic.disabled}
            onClick={mic.onClick}
            className={cn("shrink-0", mic.recording && "text-primary")}
            data-recording={mic.recording}
            data-testid={`${testId}-mic`}
            data-interactive="true"
          >
            <Icons.Mic className="h-4 w-4" />
          </Button>
        )}
        <Button
          type="button"
          variant="ghost"
          size="iconSm"
          aria-label={t("send")}
          disabled={!canSend}
          onClick={() => void send()}
          className={cn(
            "shrink-0 disabled:opacity-100",
            canSend ? "text-accent hover:text-accent" : "text-subtle",
          )}
          data-testid={`${testId}-send`}
          data-interactive="true"
        >
          <Icons.ArrowUp className="h-4 w-4" />
        </Button>
      </div>
      {sendError ? (
        <p
          role="alert"
          className="px-3 pb-2 text-appkit-caption text-danger-light"
        >
          {t("sendFailed")}
        </p>
      ) : null}
    </div>
  );
}
